import { classNames } from '@fullstory/util/fmt';
import React, { PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { identifyComponent } from '../../util';
import css from './index.module.scss';

export interface CollapseProps {
  testId?: string;
  /** Boolean determining if the Collapse is open */
  expanded: boolean;
  /** Time it takes to open/close Collapse in milliseconds */
  transitionDuration?: number;
  /** Bezier function used for opening and closing the Collapse */
  transitionTimingFunction?: string;
}

export const Collapse: React.FC<PropsWithChildren<CollapseProps>> = props => {
  const {
    testId = 'collapse',
    children,
    expanded,
    transitionDuration = 300,
    transitionTimingFunction = 'ease',
    ...rest
  } = props;

  const [height, setHeight] = useState(expanded ? 'auto' : '0px');
  const container = useRef<HTMLDivElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);

  // cannot css transition height with auto, need to measure and set height explicitly.
  // initial height is already set in the state hook, only update in this effect on
  // subsequent renders when the expanded prop changes
  useLayoutEffect(() => {
    if (!expanded && height === 'auto') {
      // collapsing, first set height back to explicit content height, measure from wrapper
      // the below effect will catch this case and zero out the height on the next event loop
      setHeight(`${wrapper.current!.clientHeight}px`);
    }

    if (expanded && height !== 'auto') {
      // expanding, transition the container height
      setHeight(`${wrapper.current!.clientHeight}px`);
    }
  }, [expanded, height]);

  // used when collapsing to zero out the height on the next event loop
  useEffect(() => {
    if (!expanded && height !== '0px') {
      // the RAF here allows the style to updated from auto
      const ref = requestAnimationFrame(() => setHeight('0px'));
      () => cancelAnimationFrame(ref);
    }
  }, [expanded, height]);

  // setting height to auto when expanded at the end of a transition allows children (content)
  // to change on the outside and have the surrounding box height re-sized as expected.
  // it also allows only doing height calcs when the expanded prop is toggled,
  // avoiding children as a dependency in this component.
  const handleOnTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.target === container.current) {
      setHeight(expanded ? 'auto' : '0px');
    }
  };

  return (
    <div
      data-component="Collapse"
      data-testid={testId}
      ref={container}
      className={classNames({
        [css.collapsed]: height != 'auto',
      })}
      onTransitionEnd={handleOnTransitionEnd}
      style={{ height, transition: `height ${transitionDuration}ms ${transitionTimingFunction}` }}
      {...rest}
      {...identifyComponent('collapse', rest)}
    >
      <div ref={wrapper}>{children}</div>
    </div>
  );
};
